/**
 * Structured patient constraints from the welcome call (2.3): availability
 * windows and days, language, gender, caregiver, SMS consent.
 *
 * **Every one of them is a requirement.** There is no hard-versus-soft class:
 * our own C2 answer lists patient constraints as hard filters, nothing Colin or
 * the RFP asked for names a soft one, and once Pete's rule that nothing blocks a
 * move is in force the two are only ever two severities of the same warning
 * (script author, discussion turn 67). Caregiver and SMS consent record
 * something without ever being checked, which is a different thing from being
 * soft. A clinical window that cannot meet patient availability is flagged to
 * the worklist, never silently scheduled over.
 */
import type { DemoState, DateKey, DemoPatient, PatientConstraint, PlanVisit, RosterEntry, WorklistItem } from "@caredna-marlin/utils/homeHealthDemo/state";
import { DAY_NAMES, daysBetweenKeys, formatInBranchZone, isoWeekday, parseDays, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";

export { parseDays };
import { effectiveVisitResources } from "./effectiveCalendar";
import { EMPTY_LOCAL_OVERLAY } from "@caredna-marlin/utils/homeHealthDemo/state";
import { atWall, joinSentences, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

export interface Violation {
  constraint: PatientConstraint;
  /** Prose, with a time token wherever a clock time belongs. The view phrases it. */
  reason: TimeSentence;
}


/** "09:00-12:00" → [540, 720] minutes. */
export function parseWindow(value: string): [number, number] | undefined {
  const m = /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return undefined;
  return [Number(m[1]) * 60 + Number(m[2]), Number(m[3]) * 60 + Number(m[4])];
}

/** The two `"HH:mm"` halves of an availability window value, so each is phrased on its own. */
function windowBounds(value: string): [string, string] {
  const [from = "", to = ""] = value.split("-").map((p) => p.trim());
  return [from, to];
}

export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** The ISO weekdays every availability-days constraint allows, or undefined when the patient named none. */
export function availableWeekdays(patient: Pick<DemoPatient, "constraints"> | undefined): number[] | undefined {
  const lists = (patient?.constraints ?? []).filter((c) => c.kind === "availabilityDays").map((c) => parseDays(c.value)).filter((days) => days.length);
  return lists.length ? [1, 2, 3, 4, 5, 6, 7].filter((d) => lists.every((allowed) => allowed.includes(d))) : undefined;
}

export interface VisitLike {
  targetDate: DateKey;
  windowStart?: string;
  windowEnd?: string;
  providerHandle?: string;
}

export function evaluateConstraints(patient: Pick<DemoPatient, "constraints">, visit: VisitLike, clinician?: RosterEntry): Violation[] {
  const out: Violation[] = [];
  for (const c of patient.constraints) {
    switch (c.kind) {
      case "availabilityDays": {
        const days = parseDays(c.value);
        if (days.length && !days.includes(isoWeekday(visit.targetDate))) out.push({ constraint: c, reason: `Patient not available on ${DAY_NAMES[isoWeekday(visit.targetDate) - 1]}` });
        break;
      }
      case "availabilityWindow": {
        const w = parseWindow(c.value);
        if (!w || !visit.windowStart || !visit.windowEnd) break;
        const vs = hhmmToMinutes(visit.windowStart);
        const ve = hhmmToMinutes(visit.windowEnd);
        // Both the visit's window and the patient's own are `"HH:mm"` values the
        // engine compares in minutes; only their phrasing follows the setting.
        if (vs < w[0] || ve > w[1])
          out.push({
            constraint: c,
            reason: ["Arrival window ", atWall(visit.windowStart), "–", atWall(visit.windowEnd), " is outside patient availability ", atWall(windowBounds(c.value)[0]), "-", atWall(windowBounds(c.value)[1])],
          });
        break;
      }
      case "language": {
        if (!clinician) break;
        const langs = clinician.languages ?? ["English"];
        if (!langs.map((l) => l.toLowerCase()).includes(c.value.toLowerCase())) out.push({ constraint: c, reason: `${clinician.displayName} does not speak ${c.value}` });
        break;
      }
      case "gender": {
        if (!clinician || !clinician.gender) break;
        if (clinician.gender !== c.value) out.push({ constraint: c, reason: `Patient requires a ${c.value === "F" ? "female" : "male"} clinician` });
        break;
      }
      case "caregiver":
      case "smsConsent":
        break; // informational
    }
  }
  return out;
}

/** Days/windows a plan visit could use inside its clinical window that satisfy the patient's availability. */
export function feasibleDaysInWindow(patient: Pick<DemoPatient, "constraints">, window: { start: DateKey; end: DateKey }, allDays: DateKey[]): DateKey[] {
  return allDays.filter((d) => d >= window.start && d <= window.end && evaluateConstraints(patient, { targetDate: d }).length === 0);
}

/** How a visit reads in a row: "recertification", "OT visit", "skilled nursing visit". */
export function visitTypeLabel(kind: PlanVisit["visitTypeKind"]): string {
  switch (kind) {
    case "SOC":
      return "start of care";
    case "ROC":
      return "resumption of care";
    case "RECERT":
      return "recertification";
    case "DISCHARGE_ASSESSMENT":
      return "discharge assessment";
    case "RN_SUPERVISORY":
      return "supervisory visit";
    case "SN_ROUTINE":
      return "skilled nursing visit";
    default: {
      const [discipline] = kind.split("_");
      if (kind.endsWith("_EVAL")) return `${discipline} evaluation`;
      return `${discipline} visit`;
    }
  }
}

/**
 * The conflict case (1.4): a clinical window that collides with the patient's
 * availability. It goes on the worklist as a row and is never silently scheduled
 * over. The row names the visit, the day it landed on and what the welcome call
 * captured — never who to call or what to do.
 */
export function availabilityConflictItem(patientRef: string, patientName: string, visit: PlanVisit, window: { start: DateKey; end: DateKey }, nowIso: string, reasons: TimeSentence[] = []): WorklistItem {
  const dated = window.start === window.end ? `is placed ${window.start}` : `has to land ${window.start}–${window.end}`;
  return {
    id: `${AVAILABILITY_ROW_PREFIX}${visit.id}`,
    kind: "availabilityConflict",
    planVisitId: visit.id,
    createdAtIso: nowIso,
    patientRef,
    episodeId: visit.episodeId,
    dueAtIso: new Date(`${window.end}T12:00:00Z`).toISOString(),
    note: reasons.length
      ? joinSentences([`${patientName}: the ${visitTypeLabel(visit.visitTypeKind)} ${dated} — `, joinSentences(reasons, "; ")], "")
      : `${patientName}: the ${visitTypeLabel(visit.visitTypeKind)} ${dated} outside the patient's availability`,
    resolved: false,
  };
}

export function summarizeConstraints(constraints: PatientConstraint[]): TimeSentence[] {
  return constraints.map((c): TimeSentence => {
    switch (c.kind) {
      case "availabilityDays":
        return `Available ${c.value}`;
      case "availabilityWindow":
        // The window's two halves are `"HH:mm"` values the checks parse; only the
        // reading of them follows the user's setting.
        return ["Available ", atWall(windowBounds(c.value)[0]), "-", atWall(windowBounds(c.value)[1])];
      case "language":
        return `Language: ${c.value}`;
      case "gender":
        return `${c.value === "F" ? "Female" : "Male"} clinician`;
      case "caregiver":
        return `Caregiver: ${c.value}`;
      case "smsConsent":
        return `SMS consent: ${c.value}`;
    }
  });
}

export interface CascadeVisit {
  visit: PlanVisit;
  /** Where the visit actually sits when the check ran (the real visit's day, if it has one). */
  placement: VisitLike;
  violations: Violation[];
}

export interface CascadeSummary {
  /** Planned visits (placeholder or real) the constraints were evaluated against. */
  checked: number;
  conflicts: CascadeVisit[];
}

/**
 * How far ahead an availability conflict is a row somebody works. A visit five
 * weeks out is a line on the patient's availability report, not an alert; the
 * lead is the same seven days the recert-window row uses, and the report always
 * shows the whole list behind it.
 */
export const AVAILABILITY_ROW_LEAD_DAYS = 7;

export const AVAILABILITY_ROW_PREFIX = "row-availability-";

type PlacementState = Partial<Pick<DemoState, "server" | "local" | "config">>;

/**
 * Where a plan visit actually sits. A visit the clinician has moved on My week
 * moves the **real** visit — the plan visit keeps its target date — so checking
 * the plan's own date would go on flagging a visit that is no longer there. The
 * check reads the real visit, with the optimistic overlay applied, so a drag
 * clears the conflict at the moment of the drop.
 */
export function effectivePlacement(state: PlacementState, visit: PlanVisit): VisitLike {
  const tz = state.config?.branch.timeZone;
  if (visit.realVisitId === undefined || !state.server || !tz) return visit;
  const vr = effectiveVisitResources({ server: state.server, local: state.local ?? EMPTY_LOCAL_OVERLAY }).find((v) => v.visitId === visit.realVisitId);
  if (!vr || vr.eventStatus === "canceled") return visit;
  return {
    targetDate: toDateKey(new Date(vr.startIso), tz),
    windowStart: formatInBranchZone(new Date(vr.startIso), tz, "HH:mm"),
    windowEnd: formatInBranchZone(new Date(vr.endIso), tz, "HH:mm"),
    providerHandle: visit.providerHandle,
  };
}

function activePlanVisitsFor(state: Pick<DemoState, "episodes" | "planVisits">, patientRef: string): PlanVisit[] {
  const episodeIds = new Set(Object.values(state.episodes).filter((e) => e.patientRef === patientRef).map((e) => e.id));
  return Object.values(state.planVisits)
    .filter((v) => episodeIds.has(v.episodeId) && (v.status === "placeholder" || v.status === "real"))
    .sort((a, b) => a.targetDate.localeCompare(b.targetDate));
}

export type CascadeState = Pick<DemoState, "episodes" | "planVisits" | "patients" | "roster"> & PlacementState;

/** 2.3.2 — structured constraints cascade to every visit: what they flag on the patient's plan today. */
export function cascadeSummary(state: CascadeState, patientRef: string): CascadeSummary {
  const patient = state.patients[patientRef];
  const out: CascadeSummary = { checked: 0, conflicts: [] };
  if (!patient) return out;
  for (const visit of activePlanVisitsFor(state, patientRef)) {
    out.checked += 1;
    const placement = effectivePlacement(state, visit);
    const violations = evaluateConstraints(patient, placement, visit.providerHandle ? state.roster[visit.providerHandle] : undefined);
    if (violations.length) out.conflicts.push({ visit, placement, violations });
  }
  return out;
}

/** The violations that are about when the patient can be seen — the ones that raise a row. */
export const AVAILABILITY_KINDS: PatientConstraint["kind"][] = ["availabilityDays", "availabilityWindow"];

export function availabilityViolations(violations: Violation[]): Violation[] {
  return violations.filter((v) => AVAILABILITY_KINDS.includes(v.constraint.kind));
}

/**
 * 2.5 — the plan checked against what the patient told us: how many future
 * plotted visits are inside the windows and which are not. The outside list is
 * the whole cert period; the alerts panel carries the ones inside the lead.
 */
export interface AvailabilityCheck {
  checked: number;
  inside: number;
  outside: CascadeVisit[];
}

export function availabilityCheck(state: CascadeState, patientRef: string, today: DateKey): AvailabilityCheck {
  const cascade = cascadeSummary(state, patientRef);
  const future = (c: CascadeVisit) => c.placement.targetDate >= today;
  const outside = cascade.conflicts.filter((c) => future(c) && availabilityViolations(c.violations).length > 0).map((c) => ({ ...c, violations: availabilityViolations(c.violations) }));
  const checked = activePlanVisitsFor(state, patientRef).filter((v) => effectivePlacement(state, v).targetDate >= today).length;
  return { checked, inside: Math.max(checked - outside.length, 0), outside };
}

/** Is this availability row still something somebody works today? */
export function availabilityRowIsLive(state: CascadeState, visit: PlanVisit | undefined, today: DateKey): { live: false } | { live: true; placement: VisitLike; violations: Violation[] } {
  if (!visit) return { live: false };
  if (visit.status !== "placeholder" && visit.status !== "real") return { live: false };
  const patient = state.patients[Object.values(state.episodes).find((e) => e.id === visit.episodeId)?.patientRef ?? ""];
  if (!patient) return { live: false };
  const placement = effectivePlacement(state, visit);
  if (placement.targetDate < today || daysBetweenKeys(today, placement.targetDate) > AVAILABILITY_ROW_LEAD_DAYS) return { live: false };
  const violations = availabilityViolations(evaluateConstraints(patient, placement, visit.providerHandle ? state.roster[visit.providerHandle] : undefined));
  return violations.length ? { live: true, placement, violations } : { live: false };
}

/** 1.4 — one worklist row per planned visit the patient's availability rules out; idempotent by visit id. */
export function planConflictItems(state: CascadeState, patientRef: string, nowIso: string): WorklistItem[] {
  const patient = state.patients[patientRef];
  if (!patient) return [];
  return cascadeSummary(state, patientRef)
    .conflicts.filter((c) => availabilityViolations(c.violations).length > 0)
    .map((c) => availabilityConflictItem(patientRef, patient.displayName, c.visit, { start: c.placement.targetDate, end: c.placement.targetDate }, nowIso, availabilityViolations(c.violations).map((v) => v.reason)));
}

/** The structured constraints mirrored back into the coordination note (or the schedule extract). */
export function coordinationNoteMirror(constraints: PatientConstraint[]): TimeSentence {
  return constraints.length ? joinSentences(["Scheduling constraints (structured): ", joinSentences(summarizeConstraints(constraints), "; "), "."], "") : "";
}
